import PropTypes from 'prop-types';
import React from 'react';
import { connect } from 'react-redux';
import { formValueSelector } from 'redux-form';
import { Chart } from 'react-google-charts';

import {
  lazyLoadPageRequest,
  pageRequest,
  tableSummaryRequest,
  itemSummaryRequest,
  topCategoriesByRequest,
  topItemsByCategoryRequest,
} from 'actions/dashboard';
import { fetchEwallet } from 'actions/setting/paymentMethod';
import { rupiah, changeRupiahToStringNum } from 'utils/formatter';
import NoDataAndLoading from 'components/NoDataAndLoading';
import { Link } from 'react-router';
import TableWithoutChild from 'components/TableReportSales/TableWithoutChild';
import EWalletNotification from 'components/EWalletNotification';
import withFeatureFlags from 'hocs/withFeatureFlags';
import { getFormattedTime } from 'utils/hourFilter';
import ChartLoadingSpinner from './ChartLoadingSpinner';
import ActionBar from './ActionBar';
import TopItemsByCategoryAllChart from './TopItemsByCategoryAllChart';
import NoData from './NoData';
import styles from './style.css';
import BigNumbersRow from './BigNumbersRow';
import DailyGrossSalesRow from './DailyGrossSalesRow';
import DayOfWeekSalesChart from './DayOfWeekSalesChart';
import HourlySalesChart from './HourlySalesChart';
import TableManagementDisplay from './TableManagementDisplay';

class Dashboard extends React.Component {
  state = {
    previousHeight: 0,
    sectionLoaded: 0,
    topItemsLoaded: false,
    topCategoriesByLoaded: false,
    topItemsByCategoryLoaded: false,
    tableManagementLoaded: false,
  };

  // need to have differend graph_id for 2 different <Dashboard component (rendered on <Layout/> - sidebar version and non version
  static renderCounter = 0;

  componentDidMount() {
    this.props.dispatch(fetchEwallet());
    // TODO HIGH change based on update_filtered_date
    if (!this.props.enableDashboardLazyLoad) {
      this.props.dispatch(pageRequest());
      this.setState({
        topItemsLoaded: true,
        topCategoriesByLoaded: true,
        topItemsByCategoryLoaded: true,
        tableManagementLoaded: true,
      });
    } else {
      this.props.dispatch(lazyLoadPageRequest());
    }
  }

  resetState = () => {
    this.setState({
      previousHeight: 0,
      sectionLoaded: 0,
      topItemsLoaded: false,
      topCategoriesByLoaded: false,
      topItemsByCategoryLoaded: false,
      tableManagementLoaded: false,
    });
  };

  onScrollListener = () => {
    const scrolled =
      (document.getElementById('scrollableHeadDiv').scrollTop +
        document.getElementById('scrollableHeadDiv').offsetHeight) /
      document.getElementById('scrollableHeadDiv').scrollHeight;

    const {
      sectionLoaded,
      previousHeight,
      topItemsLoaded,
      topCategoriesByLoaded,
      topItemsByCategoryLoaded,
      tableManagementLoaded,
    } = this.state;

    const { useReportingGraphqlApi } = this.props;

    const triggerLoadEvent =
      scrolled > 0.6 &&
      document.getElementById('scrollableHeadDiv').scrollHeight >
        previousHeight;

    if (triggerLoadEvent) {
      this.setState({
        sectionLoaded: sectionLoaded + 1,
        previousHeight: document.getElementById('scrollableHeadDiv')
          .scrollHeight,
      });
    }

    if (sectionLoaded >= 1 && topItemsLoaded === false) {
      this._getItemSummary();
    } else if (
      sectionLoaded >= 2 &&
      topCategoriesByLoaded === false &&
      useReportingGraphqlApi === true
    ) {
      this._getTopCategoriesBy();
    } else if (
      sectionLoaded >= 3 &&
      topItemsByCategoryLoaded === false &&
      useReportingGraphqlApi === true
    ) {
      this._getTopItemsByCategory();
    } else if (sectionLoaded >= 4 && tableManagementLoaded === false) {
      this._getTableManagement();
    }
  };

  _getItemSummary() {
    const { outletIds } = this.props.search;
    const {
      startDate,
      endDate,
      startHour,
      endHour,
      allDay,
    } = this.props.search.date;
    const { formattedStart, formattedEnd } = getFormattedTime(
      startDate,
      endDate,
      startHour,
      endHour,
      allDay
    );
    this.props.dispatch(
      itemSummaryRequest(formattedStart, formattedEnd, outletIds)
    );
    if (this.props.useReportingGraphqlApi) {
      this.setState({
        topItemsLoaded: true,
        previousHeight: document.getElementById('scrollableHeadDiv')
          .scrollHeight,
      });
    } else {
      this.setState({
        sectionLoaded: 3,
        topItemsLoaded: true,
        topCategoriesByLoaded: true,
        topItemsByCategoryLoaded: true,
        previousHeight: document.getElementById('scrollableHeadDiv')
          .scrollHeight,
      });
    }
  }

  _getTopCategoriesBy() {
    const { outletIds } = this.props.search;
    const {
      startDate,
      endDate,
      startHour,
      endHour,
      allDay,
    } = this.props.search.date;
    const { formattedStart, formattedEnd } = getFormattedTime(
      startDate,
      endDate,
      startHour,
      endHour,
      allDay
    );
    this.props.dispatch(
      topCategoriesByRequest(formattedStart, formattedEnd, outletIds)
    );
    this.setState({
      topCategoriesByLoaded: true,
      previousHeight: document.getElementById('scrollableHeadDiv').scrollHeight,
    });
  }

  _getTopItemsByCategory() {
    const { outletIds } = this.props.search;
    const {
      startDate,
      endDate,
      startHour,
      endHour,
      allDay,
    } = this.props.search.date;
    const { formattedStart, formattedEnd } = getFormattedTime(
      startDate,
      endDate,
      startHour,
      endHour,
      allDay
    );
    this.props.dispatch(
      topItemsByCategoryRequest(formattedStart, formattedEnd, outletIds)
    );

    this.setState({
      topItemsByCategoryLoaded: true,
      previousHeight: document.getElementById('scrollableHeadDiv').scrollHeight,
    });
  }

  _getTableManagement() {
    const { outletIds } = this.props.search;
    const {
      startDate,
      endDate,
      startHour,
      endHour,
      allDay,
    } = this.props.search.date;
    const { formattedStart, formattedEnd } = getFormattedTime(
      startDate,
      endDate,
      startHour,
      endHour,
      allDay
    );

    this.props.dispatch(
      tableSummaryRequest(formattedStart, formattedEnd, outletIds)
    );
    this.setState({
      tableManagementLoaded: true,
      previousHeight: document.getElementById('scrollableHeadDiv').scrollHeight,
    });
  }

  render() {
    const { hasActivatedEwallet } = this.props.data.setting.paymentMethod;
    const tableManagementReport = this._tableManagementReport();
    const categorySalesAmountByVolume = this._categorySalesAmountByVolume();
    const categorySalesAmountByRevenue = this._categorySalesAmountByRevenue();
    const { childBody, childBodyResponsive } = this._topItems();
    const isSubscribedTableManagement = this._isSubscribedTableManagement();
    const { kybNewContent } = this.props;
    const {
      is_kyb_registration_completed: alreadySubmitKyb,
    } = this.props.data.moka.user.business;

    const { showLoadingSpinnerPage } = this.props.data.loadingSpinner;

    const {
      gross_sales,
      net_sales,
      gross_profit,
      total_transactions,
      dailyGrossSales,
      weeklyGrossSales,
      hourlyGrossSales,
    } = this.props.data.dashboard.index;

    const showEWalletNotification =
      (!kybNewContent && !hasActivatedEwallet) ||
      (kybNewContent && !alreadySubmitKyb);

    return (
      <div
        onScroll={this.onScrollListener}
        className={styles.scrollableHeadDiv}
        id="scrollableHeadDiv"
      >
        <div className="container-full-nosub ng-scope" id="moka-dashboard">
          {showEWalletNotification && <EWalletNotification />}
          <div className="row">
            <div className={`col-xs-12 ${styles.bottom}`}>
              <div className="row">
                <ActionBar resetState={this.resetState} />
              </div>

              <div className="row">
                <div className="col-xs-12">
                  <h2 className={styles['title-section']}>SALES SUMMARY</h2>
                </div>
              </div>
              <NoDataAndLoading
                showLoadingSpinner={showLoadingSpinnerPage}
                noDataToDisplay={this.noTransaction()}
                title="No Sales"
                description=""
                linkLabel="View Dashboard"
                linkUrl="https://mokaposhelp.zendesk.com/hc/id/articles/900000459006-Cara-Melihat-Rangkuman-Laporan-pada-Dashboard"
                loadingDescription=""
                loadingDescriptionAfterChangeMenu=""
              />
              {!this.noTransaction() && (
                <div>
                  <BigNumbersRow
                    grossSales={gross_sales}
                    netSales={net_sales}
                    grossProfit={gross_profit}
                    totalTransactions={total_transactions}
                  />

                  <DailyGrossSalesRow
                    dailyGrossSales={dailyGrossSales}
                    renderCounter={Dashboard.renderCounter}
                  />

                  <div className="row">
                    <div className="col-xs-12 col-sm-6 col-md-4">
                      <DayOfWeekSalesChart
                        weeklyGrossSales={weeklyGrossSales}
                        renderCounter={Dashboard.renderCounter}
                      />
                    </div>

                    <div className="col-xs-12 col-sm-6 col-md-8">
                      <HourlySalesChart
                        hourlyGrossSales={hourlyGrossSales}
                        renderCounter={Dashboard.renderCounter}
                      />
                    </div>
                  </div>
                </div>
              )}
              <div className="row">
                <div className="col-xs-12">
                  <Link
                    className={styles['linked-dashboard']}
                    title="Click to see report!"
                    to="/reports/sales/item_sales"
                  >
                    <h2 className={styles['title-section']}>ITEM SUMMARY</h2>
                  </Link>
                </div>
              </div>
              <NoDataAndLoading
                showLoadingSpinner={showLoadingSpinnerPage}
                noDataToDisplay={this.noTransaction()}
                title="No Sales"
                description=""
                linkLabel="View Dashboard"
                linkUrl="https://mokaposhelp.zendesk.com/hc/id/articles/900000459006-Cara-Melihat-Rangkuman-Laporan-pada-Dashboard"
                loadingDescription=""
                loadingDescriptionAfterChangeMenu=""
              />
              {!this.noTransaction() && (
                <div>
                  <div className="row">
                    <div className="col-xs-12 col-md-12">
                      {this.state.topItemsLoaded && (
                        <div className={styles['sales-chart-box-top-item']}>
                          <div className={styles['title-chart-left']}>
                            TOP ITEMS
                          </div>
                          <div
                            className={`${styles.tableWrapper} visible-md visible-lg top_item_table`}
                          >
                            {childBody.length > 0 && (
                              <TableWithoutChild
                                headerBody={[
                                  'Item',
                                  'Item Sold',
                                  'Gross Sales',
                                  'Net Sales',
                                  'Gross Profit',
                                ]}
                                childBody={childBody}
                                footBody={{}}
                                tableClass="item-summary-table"
                                headerBodyClassName={[
                                  'header-item',
                                  'header-item-sold',
                                  'header-gross-sales',
                                  'header-net-saless',
                                  'header-gross-profit',
                                ]}
                                childBodyClassName={[
                                  'body-item',
                                  'body-item-sold',
                                  'body-gross-sales',
                                  'body-net-saless',
                                  'body-gross-profit',
                                ]}
                                footBodyClassName={[]}
                              />
                            )}
                            {!childBody.length && <NoData />}
                          </div>
                          <div
                            className={`${styles.tableWrapperResponsive} visible-xs visible-sm`}
                          >
                            {childBodyResponsive.length > 0 && (
                              <TableWithoutChild
                                headerBody={[
                                  'Item',
                                  'Items Sold',
                                  'Gross Sales',
                                ]}
                                childBody={childBodyResponsive}
                                footBody={{}}
                                tableClass="item-summary-table-responsive"
                                headerBodyClassName={[
                                  'header-name',
                                  'header-items-sold',
                                  'header-gross-sales',
                                ]}
                                childBodyClassName={[
                                  'body-name',
                                  'body-items-sold',
                                  'body-gross-sales',
                                ]}
                                footBodyClassName={[]}
                              />
                            )}
                            {!childBodyResponsive.length && <NoData />}
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="col-xs-12 col-md-12">
                      {this.state.topCategoriesByLoaded && (
                        <div className="row">
                          <div className="col-xs-12 col-md-6">
                            <div className={styles['sales-chart-box']}>
                              <div className={styles['title-chart-left']}>
                                CATEGORY BY VOLUME
                              </div>
                              <div className="my-pretty-chart-container">
                                {!!categorySalesAmountByVolume.rows.length && (
                                  <Chart
                                    chartType="PieChart"
                                    rows={categorySalesAmountByVolume.rows}
                                    colors={categorySalesAmountByVolume.colors}
                                    columns={
                                      this
                                        .CATEGORY_SALES_BY_VOLUMES_CHART_COLUMNS
                                    }
                                    options={
                                      this
                                        .CATEGORY_SALES_BY_VOLUMES_CHART_OPTIONS
                                    }
                                    graph_id={`area-chart-volume-${Dashboard.renderCounter}`}
                                    width="100%"
                                    height="200px"
                                    legend_toggle
                                    loader={<ChartLoadingSpinner />}
                                  />
                                )}
                                {!categorySalesAmountByVolume.rows.length && (
                                  <NoData />
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="col-xs-12 col-md-6">
                            <div className={styles['sales-chart-box']}>
                              <div className={styles['title-chart-left']}>
                                CATEGORY BY SALES
                              </div>
                              <div className="my-pretty-chart-container">
                                {!!categorySalesAmountByRevenue.rows.length && (
                                  <Chart
                                    chartType="PieChart"
                                    rows={categorySalesAmountByRevenue.rows}
                                    colors={categorySalesAmountByRevenue.colors}
                                    columns={
                                      this
                                        .CATEGORY_SALES_BY_REVENUES_CHART_COLUMNS
                                    }
                                    options={
                                      this
                                        .CATEGORY_SALES_BY_REVENUES_CHART_OPTIONS
                                    }
                                    graph_id={`area-chart-sales-${Dashboard.renderCounter}`}
                                    width="100%"
                                    height="200px"
                                    legend_toggle
                                    loader={<ChartLoadingSpinner />}
                                  />
                                )}
                                {!categorySalesAmountByRevenue.rows.length && (
                                  <NoData />
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="row">
                    <div className="col-xs-12">
                      {this.state.topItemsByCategoryLoaded && (
                        <div className="mk-sales-chart-box">
                          <div className={styles['title-chart-left']}>
                            TOP ITEMS BY CATEGORY
                          </div>
                          <div className="row">
                            <TopItemsByCategoryAllChart
                              topItemsByCategory={
                                this.props.data.dashboard.index
                                  .topItemsByCategory
                              }
                              ChartLoadingSpinner={ChartLoadingSpinner}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {isSubscribedTableManagement && (
                <div>
                  <div className="row">
                    <div className="col-xs-12">
                      <h2 className={styles['title-section']}>
                        TABLE MANAGEMENT
                      </h2>
                    </div>
                  </div>
                  <NoDataAndLoading
                    showLoadingSpinner={showLoadingSpinnerPage}
                    noDataToDisplay={this.noTableTransaction()}
                    title="No Sales"
                    description=""
                    linkLabel="View Dashboard"
                    linkUrl="https://mokaposhelp.zendesk.com/hc/id/articles/900000459006-Cara-Melihat-Rangkuman-Laporan-pada-Dashboard"
                    loadingDescription=""
                    loadingDescriptionAfterChangeMenu=""
                  />
                  {!this.noTableTransaction() &&
                    this.state.tableManagementLoaded && (
                      <TableManagementDisplay
                        tableManagementReport={tableManagementReport}
                      />
                    )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  componentDidUpdate() {
    ++Dashboard.renderCounter;
  }

  static percentageMargin(netSales, grossProfit) {
    return isNaN((grossProfit / netSales) * 100)
      ? 0
      : (grossProfit / netSales) * 100;
  }

  noTransaction() {
    const {
      dailyGrossSales,
      weeklyGrossSales,
      hourlyGrossSales,
    } = this.props.data.dashboard.index;
    return (
      dailyGrossSales.length === 0 &&
      weeklyGrossSales.length === 0 &&
      hourlyGrossSales.length === 0
    );
  }

  noTableTransaction() {
    const { tableManagementReport } = this.props.data.dashboard.index;
    return (
      !tableManagementReport.totalPax &&
      !tableManagementReport.avgRevenue &&
      !tableManagementReport.avgTime
    );
  }

  _tableManagementReport = () => {
    const { tableManagementReport } = this.props.data.dashboard.index;
    return {
      totalPax: tableManagementReport.totalPax || '0',
      avgRevenue: tableManagementReport.avgRevenue
        ? rupiah(tableManagementReport.avgRevenue)
        : 'Rp 0',
      avgTime: tableManagementReport.avgTime
        ? `${tableManagementReport.avgTime} Minutes`
        : '0 Minutes',
    };
  };

  _categorySalesAmountByRevenue = () => {
    const { categorySalesAmountByRevenue } = this.props.data.dashboard.index;
    let rows = [];
    const colors = [];
    const newColor = Dashboard.getRandomColor();
    Object.entries(categorySalesAmountByRevenue).map(([key, value]) => {
      colors.push(newColor);
      rows.push([key, value.revenue, value.pctRevenue]);
    });
    rows = rows.sort((a, b) => {
      return b[1] - a[1];
    });
    return { rows, colors };
  };

  _categorySalesAmountByVolume = () => {
    const { categorySalesAmountByVolume } = this.props.data.dashboard.index;
    let rows = [];
    const colors = [];
    const newColor = Dashboard.getRandomColor();
    Object.entries(categorySalesAmountByVolume).map(([key, value]) => {
      colors.push(newColor);
      rows.push([key, value.volume, value.pctVolume]);
    });
    rows = rows.sort((a, b) => {
      return b[1] - a[1];
    });
    return { rows, colors };
  };

  _topItems = () => {
    const topItems = this.props.data.dashboard.index.top_items;
    const childBody = [];
    const childBodyResponsive = [];
    topItems.forEach(item => {
      childBodyResponsive.push({
        name: item.name,
        item_sold: item.item_sold,
        gross_Sales: rupiah(item.gross_sales),
      });
      childBody.push({
        name: item.name,
        item_sold: item.item_sold,
        gross_Sales: changeRupiahToStringNum(item.gross_sales),
        net_sales: changeRupiahToStringNum(item.net_sales),
        gross_profit: changeRupiahToStringNum(item.gross_profit),
      });
    });
    return { childBody, childBodyResponsive };
  };

  static getRandomColor() {
    const letters = '0123456789ABCDEF';
    let color = '#';
    for (let i = 0; i < 6; i++) {
      color += letters[Math.floor(Math.random() * 16)];
    }
    return color;
  }

  _isSubscribedTableManagement = () => {
    const { user } = this.props.data.moka;
    const featureSubscription = user.feature_subscription;
    const tableManagementFeature = 'Table Management';

    if (featureSubscription) {
      return featureSubscription.some(
        e => e.feature_name === tableManagementFeature && e.is_subscribing
      );
    }
  };

  CATEGORY_SALES_BY_REVENUES_CHART_OPTIONS = {
    isStacked: true,
    fill: 20,
    displayExactValues: true,
    vAxis: { title: 'Category Sales by Sales', gridlines: { count: 0 } },
    hAxis: { title: 'Date' },
    is3D: false,
    chartArea: {
      left: 5,
      top: 10,
      bottom: 10,
      width: '100%',
      height: '350',
    },
  };

  CATEGORY_SALES_BY_REVENUES_CHART_COLUMNS = [
    { id: 'item', label: 'Item', type: 'string' },
    { id: 'sales', label: 'Sales', type: 'number' },
    { id: 'percent', label: 'Percent', type: 'number' },
  ];

  CATEGORY_SALES_BY_VOLUMES_CHART_OPTIONS = {
    isStacked: true,
    fill: 20,
    displayExactValues: true,
    vAxis: { title: 'Category Sales by Volume', gridlines: { count: 20 } },
    hAxis: { title: 'Date' },
    is3D: false,
    chartArea: {
      left: 5,
      top: 10,
      bottom: 10,
      width: '100%',
      height: '350',
    },
  };

  CATEGORY_SALES_BY_VOLUMES_CHART_COLUMNS = [
    { id: 'item', label: 'Item', type: 'string' },
    { id: 'sales', label: 'Sales', type: 'number' },
    { id: 'percent', label: 'Percent', type: 'number' },
  ];

  TOP_ITEMS_CHART_COLUMNS = [
    { type: 'string', label: 'Item' },
    { type: 'number', label: 'Volume' },
    { type: 'number', label: 'Sales' },
  ];
}

Dashboard.propTypes = {
  data: PropTypes.object,
  history: PropTypes.object,
  dispatch: PropTypes.func,
  rupiah: PropTypes.func,
};

const selector = formValueSelector('search');

// Which props do we want to inject, given the global state?
function select(state) {
  return {
    data: state,
    search: {
      date: selector(state, 'date'),
      outletIds: selector(state, 'outlet'),
    },
  };
}

function mapFeatureFlags(flags) {
  return {
    kybNewContent: flags.kybNewContent,
    useReportingGraphqlApi: flags.useReportingGraphqlApi,
    enableDashboardLazyLoad: flags.enableDashboardLazyLoad,
  };
}

// Wrap the component to inject dispatch and state into it
export default withFeatureFlags(mapFeatureFlags)(connect(select)(Dashboard));
